/**
 * Безопасное экранирование значений для PostgREST `.or()` фильтра.
 *
 * ## Проблема
 *
 * `supabase-js` в текущей версии не санитизирует значения внутри `.or()`:
 *   .or(`user_id.eq.${userId},friend_id.eq.${userId}`)
 *
 * Если `userId = "0,user_id.gte.1"` — фильтр расширяется до:
 *   or=(user_id.eq.0,user_id.gte.1,friend_id.eq.0,user_id.gte.1)
 *
 * Атакующий может **расширить** условие через запятую и вытащить чужие данные.
 * Это не классическая SQL injection (PostgREST ограниченнее SQL), но такой же
 * риск эксфильтрации. См. Supabase GitHub Discussion #3843.
 *
 * ## Решение
 *
 * Две линии защиты:
 *
 * 1. **Валидация что value это UUID** — если нет, выбрасываем ошибку ДО запроса.
 *    UUID имеет фиксированный формат `xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx`,
 *    не содержит запятых, точек, кавычек — подмешать туда injection невозможно.
 *
 * 2. **Двойные кавычки вокруг value** — PostgREST трактует содержимое в `"..."`
 *    как **литерал**, а не как выражение. Даже если регулярка пропустит что-то
 *    странное (напр. UUID с неожиданным символом), кавычки блокируют эскейп.
 *
 * Defense in depth: первая линия отсекает 99% атак, вторая — страховочная.
 *
 * ## Использование
 *
 * ```ts
 * // Было:
 * .or(`user_id.eq.${userId},friend_id.eq.${userId}`)
 *
 * // Стало:
 * .or(orFilterUuid([
 *   { column: 'user_id', uuid: userId },
 *   { column: 'friend_id', uuid: userId },
 * ]))
 * ```
 *
 * Для сложных случаев с `and(...)` / `in.(...)` используй `orFilterRaw` —
 * там нужно самому убедиться что все UUID валидны (функция проверит).
 *
 * Референсы:
 *   - https://github.com/orgs/supabase/discussions/3843 (проблема)
 *   - https://postgrest.org/en/stable/references/api/tables_views.html#horizontal-filtering (синтаксис)
 */

/**
 * Экранирует свободный текст (от юзера) для использования в PostgREST `ilike`/`like`.
 *
 * Проблема: юзер вводит в поиск `%,display_name.eq.admin`. Без экранирования
 * это превращается в расширенный фильтр — PostgREST injection.
 *
 * Решение:
 *   1. Удаляем спец-символы PostgREST которые могли бы сломать грамматику:
 *      `,` — разделитель фильтров в `.or()`
 *      `(` `)` — группировка
 *      `.` — разделитель оператора от значения в старом синтаксисе
 *      `"` — граница литерала (ломает нашу обёртку)
 *      `*` — wildcard в PostgREST (замещается на `%` при ilike)
 *   2. Сохраняем `%` и `_` — это легитимные wildcard для ilike.
 *   3. Оборачиваем в двойные кавычки (PostgREST трактует как литерал).
 *
 * Пример:
 *   escapeLikePattern('John%Doe')       → '"%John%Doe%"'
 *   escapeLikePattern(',username.eq.x') → '"%usernameeqx%"'
 */
export function escapeLikePattern(raw: string, mode: 'contains' | 'exact' = 'contains'): string {
  const stripped = raw.replace(/[,().*"`\\]/g, '');
  if (mode === 'exact') return `"${stripped}"`;
  return `"%${stripped}%"`;
}

/** Regex UUID v1-v8 (общий для всех версий). */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_REGEX.test(value);
}

/**
 * Throws `Error` если value не UUID. Используй перед тем как подставлять в .or().
 */
export function assertUuid(value: unknown, context?: string): asserts value is string {
  if (!isValidUuid(value)) {
    throw new Error(`Invalid UUID${context ? ` (${context})` : ''}: ${String(value).slice(0, 40)}`);
  }
}

/**
 * Проверяет массив UUID. Отфильтровывает не-UUID с warning в консоль.
 * Возвращает чистый массив UUID готовый к `.in.(...)`.
 *
 * Не throws — просто игнорирует мусор. Это нужно для случаев типа collabWishlistIds
 * где массив может быть пустым или содержать legacy-значения.
 */
export function filterUuidArray(values: unknown[]): string[] {
  const valid: string[] = [];
  for (const v of values) {
    if (isValidUuid(v)) {
      valid.push(v);
    } else {
      console.warn('[filterUuidArray] Non-UUID value skipped:', String(v).slice(0, 40));
    }
  }
  return valid;
}

export interface UuidFilter {
  /** Имя колонки (статическое, из кода — никогда не юзер-инпут). */
  column: string;
  /** UUID для фильтрации. Будет провалидирован. */
  uuid: string;
}

/**
 * Собирает безопасный PostgREST-фильтр для `.or()` из списка UUID-условий.
 *
 * Каждый UUID валидируется + оборачивается в двойные кавычки.
 * Если любой UUID невалидный — throws, запрос не выполнится.
 *
 * Пример:
 *   orFilterUuid([
 *     { column: 'user_id', uuid: 'abc-...' },
 *     { column: 'friend_id', uuid: 'abc-...' },
 *   ])
 *   // → 'user_id.eq."abc-...",friend_id.eq."abc-..."'
 */
export function orFilterUuid(filters: UuidFilter[]): string {
  return filters
    .map(({ column, uuid }) => {
      assertUuid(uuid, column);
      return `${column}.eq."${uuid}"`;
    })
    .join(',');
}

/**
 * Собирает безопасный `id.in.(...)` фильтр из массива UUID.
 *
 * Не-UUID элементы игнорируются (с warning). Если после фильтрации массив пуст,
 * возвращает `null` — вызывающий код должен это обработать и не делать `.or()`.
 *
 * Пример:
 *   const safeIn = inFilterUuid('id', ['abc-...', 'def-...']);
 *   // → 'id.in.("abc-...","def-...")'
 */
export function inFilterUuid(column: string, uuids: string[]): string | null {
  const valid = filterUuidArray(uuids);
  if (valid.length === 0) return null;
  const quoted = valid.map((u) => `"${u}"`).join(',');
  return `${column}.in.(${quoted})`;
}

/**
 * Экранирует UUID для использования внутри `eq.`, `neq.` и т.п. — с двойными кавычками.
 * Для одиночных значений в простых фильтрах (не через .or).
 *
 * Пример:
 *   .or(`organizer_id.eq.${safeUuid(userId)},id.in.(...)`)
 */
export function safeUuid(uuid: string): string {
  assertUuid(uuid);
  return `"${uuid}"`;
}